import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  AlertTriangle, CalendarDays, Loader2, MoreHorizontal, Pause, Pencil, Play, Repeat, RotateCw, SkipForward, Square, User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { EmptyState, PersonAvatar, StatusChip, adminButton, adminLink, type AdminTone } from '@/components/admin/ui';
import {
  useExtendSeries,
  useSetSeriesStatus,
  useSkipOccurrence,
  type RecurringSeriesRow,
  type SeriesAction,
  type TeamMemberLite,
} from '@/hooks/useRecurringSeries';
import { SERIES_STATUS_LABELS, describeSchedule, type SeriesStatus } from '@/lib/recurrence';
import { cn } from '@/lib/utils';
import { SeriesEditDialog } from './SeriesEditDialog';
import { errorText } from './visit-edit';

const STATUS_TONE: Record<SeriesStatus, AdminTone> = {
  pending: 'violet',
  active: 'green',
  paused: 'amber',
  ended: 'grey',
};

const pretty = (d: string) => format(parseISO(d), 'EEE d MMM yyyy');

interface RepeatCleansListProps {
  rows: RecurringSeriesRow[];
  teamMembers: TeamMemberLite[];
  canWrite: boolean;
  /** Series to scroll to and highlight (deep link from a job). */
  highlightId?: string | null;
  /** Hide the client column (on the customer record it's always the same person). */
  hideClient?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
}

/** Repeat cleans with schedule, next visit, cleaner, status and actions. */
export function RepeatCleansList({
  rows,
  teamMembers,
  canWrite,
  highlightId,
  hideClient,
  emptyTitle = 'No repeat cleans yet.',
  emptyDescription = 'Set a job to repeat from Jobs & Calendar, or convert an enquiry that asked for regular cleans.',
}: RepeatCleansListProps) {
  const { toast } = useToast();
  const setStatus = useSetSeriesStatus();
  const skip = useSkipOccurrence();
  const extend = useExtendSeries();
  const [editing, setEditing] = useState<RecurringSeriesRow | null>(null);
  const [ending, setEnding] = useState<RecurringSeriesRow | null>(null);
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const highlightRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightId, rows.length]);

  const run = async (key: string, fn: () => Promise<unknown>, success: string, failure: string) => {
    setBusyKey(key);
    try {
      await fn();
      toast({ title: success });
      return true;
    } catch (error) {
      toast({ title: failure, description: errorText(error), variant: 'destructive' });
      return false;
    } finally {
      setBusyKey(null);
    }
  };

  const changeStatus = (row: RecurringSeriesRow, action: SeriesAction, until?: string) =>
    run(
      `${row.id}:${action}`,
      () => setStatus.mutateAsync({ seriesId: row.id, action, endDate: until }),
      action === 'pause' ? 'Repeat clean paused' : action === 'resume' ? 'Repeat clean resumed' : 'Repeat clean ended',
      'Couldn’t update the repeat clean',
    );

  if (rows.length === 0) {
    return <EmptyState icon={<Repeat />} title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <>
      <ul className="adm-divide" data-testid="repeat-cleans-list">
        {rows.map((row) => {
          const highlighted = row.id === highlightId;
          const busy = busyKey?.startsWith(row.id) ?? false;
          return (
            <li
              key={row.id}
              ref={highlighted ? highlightRef : undefined}
              data-testid={`repeat-clean-${row.id}`}
              className={cn('px-4 py-3.5 md:px-5', highlighted && 'bg-secondary/40')}
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  {!hideClient && <PersonAvatar name={row.client_name || row.title} seed={row.customer_id ?? row.id} size={34} />}
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-[13.5px] font-semibold text-foreground">
                        {hideClient ? row.title : row.client_name || row.title}
                      </span>
                      <StatusChip tone={STATUS_TONE[row.status]}>{SERIES_STATUS_LABELS[row.status]}</StatusChip>
                    </p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                      <Repeat className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span data-testid={`repeat-clean-schedule-${row.id}`}>
                        {describeSchedule(row.frequency, row.anchor_date, row.start_time)}
                        {!hideClient && row.title && row.client_name ? ` · ${row.title}` : ''}
                        {row.end_date ? ` · until ${format(parseISO(row.end_date), 'd MMM yyyy')}` : ''}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12.5px] md:w-[360px] md:shrink-0">
                  <div className="min-w-0">
                    <p className="text-[11.5px] font-medium text-muted-foreground">Next visit</p>
                    <p className="flex items-center gap-1 truncate font-medium text-foreground" data-testid={`repeat-clean-next-${row.id}`}>
                      <CalendarDays className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      {row.nextVisit ? (
                        <Link
                          to={`/admin/schedule?view=team&date=${row.nextVisit.scheduled_date}&job=${row.nextVisit.id}`}
                          className="truncate hover:underline"
                        >
                          {format(parseISO(row.nextVisit.scheduled_date), 'EEE d MMM')} · {row.nextVisit.start_time.slice(0, 5)}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">None booked</span>
                      )}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11.5px] font-medium text-muted-foreground">Cleaner</p>
                    <p className="flex items-center gap-1 truncate font-medium text-foreground">
                      <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="truncate">{row.cleanerNames.length ? row.cleanerNames.join(', ') : 'Unassigned'}</span>
                    </p>
                  </div>
                </div>

                {canWrite && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(adminButton.outline, 'self-start md:self-center')}
                        aria-label={`Actions for ${row.client_name || row.title}`}
                        disabled={busy}
                      >
                        {busy ? <Loader2 className="animate-spin" /> : <MoreHorizontal />}
                        <span className="md:sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {row.status !== 'ended' && (
                        <DropdownMenuItem onSelect={() => setEditing(row)}>
                          <Pencil className="mr-2 h-4 w-4" /> Edit schedule
                        </DropdownMenuItem>
                      )}
                      {row.status === 'active' && (
                        <DropdownMenuItem onSelect={() => changeStatus(row, 'pause')}>
                          <Pause className="mr-2 h-4 w-4" /> Pause
                        </DropdownMenuItem>
                      )}
                      {row.status === 'paused' && (
                        <DropdownMenuItem onSelect={() => changeStatus(row, 'resume')}>
                          <Play className="mr-2 h-4 w-4" /> Resume
                        </DropdownMenuItem>
                      )}
                      {row.status !== 'ended' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => {
                              setEndDate(format(new Date(), 'yyyy-MM-dd'));
                              setEnding(row);
                            }}
                          >
                            <Square className="mr-2 h-4 w-4" /> End repeat clean
                          </DropdownMenuItem>
                        </>
                      )}
                      {row.status === 'ended' && (
                        <DropdownMenuItem disabled>This repeat clean has ended</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {row.status === 'pending' && (
                <p className="mt-2 text-[12.5px] text-muted-foreground">
                  Booked online. Approve the first visit in{' '}
                  <Link to="/admin/schedule" className={cn(adminLink, 'px-0')}>Jobs &amp; Calendar</Link>{' '}
                  and the rest will be added automatically.
                </p>
              )}

              {row.clashes.length > 0 && (
                <div
                  className="mt-3 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                  data-testid={`repeat-clean-clashes-${row.id}`}
                >
                  <p className="flex items-center gap-1.5 font-semibold">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                    {row.clashes.length === 1 ? '1 visit couldn’t be booked' : `${row.clashes.length} visits couldn’t be booked`} without double-booking
                  </p>
                  <ul className="mt-1.5 space-y-1.5">
                    {row.clashes.map((c) => (
                      <li key={c.occurrence_date} className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{pretty(c.occurrence_date)}</span>
                        {c.clash_detail && <span className="opacity-80">· {c.clash_detail}</span>}
                        {canWrite && (
                          <span className="ml-auto flex gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={cn(adminButton.outline, 'h-7 px-2 text-[12px]')}
                              onClick={() =>
                                run(`${row.id}:retry`, () => extend.mutateAsync(row.id), 'Checked again', 'Couldn’t check again')
                              }
                              disabled={busy}
                            >
                              <RotateCw /> Try again
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={cn(adminButton.outline, 'h-7 px-2 text-[12px]')}
                              onClick={() =>
                                run(
                                  `${row.id}:skip`,
                                  () => skip.mutateAsync({ seriesId: row.id, occurrenceDate: c.occurrence_date }),
                                  'Date skipped',
                                  'Couldn’t skip the date',
                                )
                              }
                              disabled={busy}
                            >
                              <SkipForward /> Skip date
                            </Button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 opacity-80">We’ll try again each day in case the other job moves.</p>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <SeriesEditDialog
        series={editing}
        teamMembers={teamMembers}
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(null)}
      />

      <Dialog open={Boolean(ending)} onOpenChange={(open) => !open && setEnding(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>End this repeat clean?</DialogTitle>
            <DialogDescription>
              Upcoming visits after the date below are removed. Visits you’ve changed by hand, and anything already done, are kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="series-end-date">Last visit on or before</Label>
            <Input id="series-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setEnding(null)}>
              Keep it going
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="h-9 rounded-lg px-3.5 text-[13px] font-semibold"
              disabled={!endDate || Boolean(busyKey)}
              onClick={async () => {
                if (!ending) return;
                const ok = await changeStatus(ending, 'end', endDate);
                if (ok) setEnding(null);
              }}
            >
              {busyKey?.endsWith(':end') && <Loader2 className="h-4 w-4 animate-spin" />}
              End repeat clean
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
